// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: the pictures, stylesheets and fonts an HTML page loads from beside it
// (`<img src="shots/a.png">`, `<link rel="stylesheet" href="../css/site.css">`), for pages whose
// source can hand them over (an agent's page, read through its machine). The preview's policy
// blocks every URL, so the page is rewritten first: stylesheets go inline, the small files they use
// become data: URLs, and pictures either come along as data: URLs (a still thumbnail, first few
// only) or are fetched as the viewer scrolls to them and handed to the frame as blobs.

/** Gives the bytes of a file a page names: relative to the page's folder (may start with ../), or absolute. */
export type HtmlAssetLoader = (path: string, signal: AbortSignal) => Promise<Blob>;

/** `outline`: styled, pictures still to come (shown first); `thumbnail`: with its first pictures; `page`: pictures load in the frame. */
export type HtmlAssetMode = 'outline' | 'thumbnail' | 'page';

export interface PreparedHtml {
	html: string;
	/** Files the page frame asks for as they near the screen, by the id on their elements. */
	lazy: Map<string, string>;
	/** Local pictures and videos the page names. */
	pictures: number;
}

const STYLESHEETS_MAX = 12;
const STYLESHEET_MAX_BYTES = 1024 * 1024;
/** Files a stylesheet or style attribute uses, inlined as data: URLs. */
const INLINE_MAX_BYTES = 2 * 1024 * 1024;
const INLINE_BUDGET_BYTES = 6 * 1024 * 1024;
/** Pictures a thumbnail carries, in page order, and how large they may be. */
const THUMBNAIL_PICTURES = 6;
const THUMBNAIL_PICTURE_MAX_BYTES = 1.5 * 1024 * 1024;
const THUMBNAIL_BUDGET_BYTES = 3 * 1024 * 1024;
/** A grey box where a picture is still to come (or stays out), so the page keeps its shape. */
const PLACEHOLDER_CSS =
	'img:not([src]),input[type="image" i]:not([src]),video:not([poster]):not([src]){min-width:60px;min-height:90px;background:rgba(128,128,128,.14);border-radius:4px}';

/** The element attribute that marks a lazily loaded file, and the one that names which attribute it fills. */
export const LAZY_ID = 'data-konvo-asset';
export const LAZY_ATTR = 'data-konvo-asset-attr';

const SCHEME = /^[a-z][a-z0-9+.-]*:/i;
const CSS_URL = /url\(\s*(['"]?)([^'")]+?)\1\s*\)/gi;

/** `ref` from inside `dir` (a folder relative to the page's, or absolute), as one path. */
export function joinAssetPath(dir: string, ref: string): string {
	const absolute = ref.startsWith('/') || (!!dir && dir.startsWith('/'));
	const parts = ref.startsWith('/') ? ref.split('/') : [...dir.split('/'), ...ref.split('/')];
	const out: Array<string> = [];
	for (const part of parts) {
		if (!part || part === '.') continue;
		if (part === '..') {
			if (out.length && out[out.length - 1] !== '..') out.pop();
			else if (!absolute) out.push('..');
		} else out.push(part);
	}
	return (absolute ? '/' : '') + out.join('/');
}

/** The folder of a path from joinAssetPath. */
const folderOf = (path: string) => {
	const slash = path.lastIndexOf('/');
	return slash < 0 ? '' : path.slice(0, slash) || '/';
};

/** A local file a page names (not a web address, data or an anchor), as a path from the page's folder; else null. */
export function localAssetPath(raw: string, dir = ''): string | null {
	const value = raw.trim();
	if (!value || value.startsWith('#') || value.startsWith('//')) return null;
	let path = value;
	if (/^file:/i.test(value)) path = value.replace(/^file:\/\/(localhost)?/i, '');
	else if (SCHEME.test(value)) return null;
	path = path.replace(/[?#].*$/, '');
	try {
		path = decodeURIComponent(path);
	} catch {}
	if (!path || path.includes('\0') || path.endsWith('/')) return null;
	return joinAssetPath(dir, path);
}

const dataUrl = (blob: Blob) =>
	new Promise<string>((resolve, reject) => {
		const reader = new FileReader();
		reader.onload = () => resolve(String(reader.result));
		reader.onerror = () => reject(reader.error);
		reader.readAsDataURL(blob);
	});

/** Loads and keeps track of bytes inlined so far. */
class Inliner {
	private used = 0;
	private readonly loaded = new Map<string, Promise<string | null>>();
	constructor(
		private readonly load: HtmlAssetLoader,
		private readonly signal: AbortSignal,
		private readonly budget: number,
		private readonly maxEach: number,
	) {}

	dataUrl(path: string): Promise<string | null> {
		let pending = this.loaded.get(path);
		if (!pending) {
			pending = this.load(path, this.signal)
				.then((blob) => {
					if (blob.size > this.maxEach || this.used + blob.size > this.budget) return null;
					this.used += blob.size;
					return dataUrl(blob);
				})
				.catch(() => null);
			this.loaded.set(path, pending);
		}
		return pending;
	}
}

/** CSS with each local url() it uses as a data: URL (or dropped when it can't be had). */
async function inlineCss(css: string, dir: string, inliner: Inliner): Promise<string> {
	const paths = new Map<string, string | null>();
	for (const match of css.matchAll(CSS_URL)) {
		const path = localAssetPath(match[2], dir);
		if (path && !paths.has(path)) paths.set(path, null);
	}
	await Promise.all([...paths.keys()].map(async (path) => paths.set(path, await inliner.dataUrl(path))));
	return css.replace(CSS_URL, (whole, _quote, ref) => {
		const path = localAssetPath(ref, dir);
		if (!path) return whole;
		const url = paths.get(path);
		return url ? `url("${url}")` : 'url("")';
	});
}

/** Picture and video attributes that load a file, by element. */
const MEDIA_ATTRIBUTES: Array<[selector: string, attribute: string]> = [
	['img[src]', 'src'],
	['input[type="image" i][src]', 'src'],
	['video[poster]', 'poster'],
	['video[src]', 'src'],
	['video > source[src]', 'src'],
	['image[href]', 'href'],
];

/**
 * The page rewritten to carry its local files: stylesheets inline and, for a thumbnail, its first
 * pictures as data: URLs. For the page itself, pictures and videos are marked for loading as the
 * frame scrolls to them (see HTML_ASSET_SCRIPT). Files that can't be had are left out quietly.
 */
export async function prepareHtmlAssets(
	html: string,
	load: HtmlAssetLoader,
	mode: HtmlAssetMode,
	signal: AbortSignal,
): Promise<PreparedHtml> {
	const doc = new DOMParser().parseFromString(html, 'text/html');
	const inliner = new Inliner(load, signal, INLINE_BUDGET_BYTES, INLINE_MAX_BYTES);
	const lazy = new Map<string, string>();

	const sheets = [...doc.querySelectorAll<HTMLLinkElement>('link[rel~="stylesheet" i][href]')];
	await Promise.all(
		sheets.map(async (link, index) => {
			const path = localAssetPath(link.getAttribute('href') ?? '');
			if (!path) return;
			let css = '';
			if (index < STYLESHEETS_MAX) {
				try {
					const blob = await load(path, signal);
					if (blob.size <= STYLESHEET_MAX_BYTES) css = await inlineCss(await blob.text(), folderOf(path), inliner);
				} catch {}
			}
			const style = doc.createElement('style');
			style.textContent = css.replace(/<\/(style)/gi, '<\\/$1');
			const media = link.getAttribute('media');
			if (media) style.setAttribute('media', media);
			link.replaceWith(style);
		}),
	);
	await Promise.all([
		...[...doc.querySelectorAll('style')].map(async (style) => {
			const css = style.textContent ?? '';
			if (/url\(/i.test(css)) style.textContent = await inlineCss(css, '', inliner);
		}),
		...[...doc.querySelectorAll<HTMLElement>('[style]')]
			.filter((element) => /url\(/i.test(element.getAttribute('style') ?? ''))
			.map(async (element) => {
				element.setAttribute('style', await inlineCss(element.getAttribute('style') ?? '', '', inliner));
			}),
	]);

	// Responsive picture lists with local files can't be loaded lazily; the plain src stays.
	for (const element of doc.querySelectorAll('[srcset]'))
		if (
			(element.getAttribute('srcset') ?? '')
				.split(',')
				.some((entry) => localAssetPath(entry.trim().split(/\s+/)[0] ?? '') !== null)
		) {
			if (element.localName === 'source' && element.parentElement?.localName === 'picture') element.remove();
			else element.removeAttribute('srcset');
		}

	const pictures = new Inliner(load, signal, THUMBNAIL_BUDGET_BYTES, THUMBNAIL_PICTURE_MAX_BYTES);
	const found = new Set<Element>();
	const thumbnails: Array<Promise<void>> = [];
	let next = 0;
	for (const [selector, attribute] of MEDIA_ATTRIBUTES) {
		for (const element of doc.querySelectorAll(selector)) {
			const path = localAssetPath(element.getAttribute(attribute) ?? '');
			if (!path) continue;
			element.removeAttribute(attribute);
			// One file per element (a video's poster before its own source).
			if (element.hasAttribute(LAZY_ID)) continue;
			found.add(element);
			if (mode === 'outline') continue;
			if (mode === 'thumbnail') {
				// Only pictures, the first few: a thumbnail never plays video.
				const picture = element.localName !== 'video' && element.localName !== 'source';
				if (picture && thumbnails.length < THUMBNAIL_PICTURES)
					thumbnails.push(
						pictures.dataUrl(path).then((url) => {
							if (url) element.setAttribute(attribute, url);
						}),
					);
				continue;
			}
			const id = String(++next);
			lazy.set(id, path);
			element.setAttribute(LAZY_ID, id);
			element.setAttribute(LAZY_ATTR, attribute);
		}
	}
	await Promise.all(thumbnails);
	if (found.size) {
		const placeholders = doc.createElement('style');
		placeholders.textContent = PLACEHOLDER_CSS;
		doc.head.prepend(placeholders);
	}
	if (mode === 'page' && lazy.size) {
		const script = doc.createElement('script');
		script.textContent = HTML_ASSET_SCRIPT;
		doc.body.append(script);
	}
	return {html: `<!doctype html>\n${doc.documentElement.outerHTML}`, lazy, pictures: found.size};
}

/**
 * Runs inside the page frame: hands Konvo a private channel once, then asks over it for each marked
 * file as it nears the screen and puts the blob it gets back in place. Files only travel over that
 * channel, so they reach this document and no page the frame may navigate to later.
 */
export const HTML_ASSET_SCRIPT = `(function(){
var ID=${JSON.stringify(LAZY_ID)},ATTR=${JSON.stringify(LAZY_ATTR)};
var channel=new MessageChannel(),port=channel.port1;
parent.postMessage({konvoAssets:1},'*',[channel.port2]);
port.onmessage=function(event){
var data=event.data;
if(!data||typeof data.konvoAsset!=='string'||!(data.blob instanceof Blob))return;
var url=URL.createObjectURL(data.blob);
document.querySelectorAll('['+ID+'="'+CSS.escape(data.konvoAsset)+'"]').forEach(function(element){
element.setAttribute(element.getAttribute(ATTR),url);
if(element.localName==='source'&&element.parentElement&&element.parentElement.load)element.parentElement.load();
});
};
function ask(element){port.postMessage({konvoAsset:element.getAttribute(ID)});}
var observer=typeof IntersectionObserver==='function'?new IntersectionObserver(function(entries){
entries.forEach(function(entry){if(entry.isIntersecting){observer.unobserve(entry.target);ask(entry.target);}});
},{rootMargin:'1200px 0px'}):null;
document.querySelectorAll('['+ID+']').forEach(function(element){observer?observer.observe(element):ask(element);});
})();`;
